import { HelpCircle } from "@untitledui/icons";
import type { ReactNode, Ref } from "react";
import type { LabelProps as AriaLabelProps } from "react-aria-components";
import { Label as AriaLabel } from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

/**
 * True when a required field counts as populated.
 * Empty: null, undefined, NaN, false, "", whitespace-only text, or an empty array.
 * Filled: any other string (including "0"), any finite number (including 0), true,
 * a non-empty array, or any other non-null value.
 */
export function isRequiredFieldFilled(value: unknown): boolean {
	if (value == null) return false;
	if (typeof value === "number") return Number.isFinite(value);
	if (typeof value === "boolean") return value;
	if (typeof value === "string") return value.trim().length > 0;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}

export function FieldLabelAsterisk({
	isFilled,
	isRequired,
}: {
	isFilled: boolean;
	isRequired?: boolean;
}) {
	return (
		<span
			className={cx(
				"hidden text-sm font-medium",
				isFilled ? "text-brand-secondary" : "text-error-primary",
				isRequired && "block",
				typeof isRequired === "undefined" && "group-required:block",
			)}
			aria-hidden="true"
		>
			*
		</span>
	);
}

export interface FieldLabelProps extends AriaLabelProps {
	children: ReactNode;
	isRequired?: boolean;
	/** When true, the required asterisk uses the filled (brand) color. */
	isFilled?: boolean;
	tooltip?: string;
	tooltipDescription?: string;
	ref?: Ref<HTMLLabelElement>;
}

export const FieldLabel = ({
	isRequired,
	isFilled = false,
	tooltip,
	tooltipDescription,
	className,
	children,
	...props
}: FieldLabelProps) => {
	return (
		<AriaLabel
			// Used for conditionally hiding/showing the label element via CSS:
			// <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
			// or
			// <Input label="Visible only on mobile" className="lg:label:hidden" />
			data-label="true"
			{...props}
			className={cx(
				// Figma FieldLabel: Text sm/Bold, text-secondary; asterisk is Text sm/Medium
				"flex cursor-default items-center gap-0.5 text-sm font-bold text-secondary",
				className,
			)}
		>
			{children}

			<FieldLabelAsterisk isFilled={isFilled} isRequired={isRequired} />

			{tooltip && (
				<Tooltip
					title={tooltip}
					description={tooltipDescription}
					placement="top"
				>
					<TooltipTrigger
						// `TooltipTrigger` inherits the disabled state from the parent form field
						// but we don't that. We want the tooltip be enabled even if the parent
						// field is disabled.
						isDisabled={false}
						className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
					>
						<HelpCircle className="size-4" />
					</TooltipTrigger>
				</Tooltip>
			)}
		</AriaLabel>
	);
};

FieldLabel.displayName = "FieldLabel";
